import React, { useContext, useEffect, useMemo, useState } from 'react';
import { connect, DispatchProp, useSelector } from 'react-redux';
import {
  CBadge,
  CButton,
  CCard,
  CCardHeader,
  CCol,
  CDataTable,
  CFormGroup,
  CImg,
  CInputCheckbox,
  CRow,
  CTooltip,
} from '@coreui/react';
import { find } from 'lodash';
import * as pluralize from 'pluralize';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RootState } from '../../store/state';
import { UserInfo, UserRoles } from '../../shared/models/user';
import { LoadUsers, RemoveUsers } from '../actions/userManagement.action';
import AddTechnicianModal from './modals/AddTechnicianModal';
import TechnicianActions from '../components/TechnicianActions';
import emptyAvatar from '../../assets/images/empty-avatar.png';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import TableStats from '../../shared/components/TableStats';
import { isActionAllowed } from '../../shared/utils/permissions-utils';
import tooltip from '../../assets/images/tool_tip.svg';
import { selectUserOrganization } from '../../store/selectors/store.selectors';
import { selectAgentCount } from '../../store/selectors/user.selectors';
import { selectOrgHierarchySelectedOrg, selectOrgList } from '../../store/selectors/orgHierarchy.selectors';
import OrgHierarchyFilter from '../../shared/components/orgHierarchy/OrgHierarchyFilter';
import { OpenModal } from '../../store/actions/modal.action';
import { ConfirmationDataBag, ModalName } from '../../shared/models/modal';

const ITEMS_PER_PAGE = 20;

interface BulkActionState {
  action: string;
  selected: UserInfo[];
}

const MASS_DELETE_ACTION = labels => ({
  label: 'Mass Delete',
  action: (dispatch, agents: UserInfo[], clearBulkAction) => dispatch(new OpenModal(ModalName.Confirmation, {
    title: `Delete ${pluralize(labels.agent, agents.length, true)}`,
    message: `Are you sure you want to delete selected  ${pluralize(labels.agent, agents.length).toLowerCase()}? You will permanently lose their ${pluralize("profiles", agents.length).toLowerCase()} and photos.`,
    confirmAction: () => {
      dispatch(new RemoveUsers({ids: agents.map(agent => `${agent.id}`), includeChildren: true}));
      clearBulkAction?.();
    },
  } as ConfirmationDataBag)),
});

export interface UsersViewProps extends DispatchProp {
  users: UserInfo[];
  isLoading: boolean;
  profile: UserInfo;
}

const TechniciansListPage = (props: UsersViewProps) => {
  const {
    users,
    isLoading,
    profile,
    dispatch,
  } = props;

  const org = useSelector(selectUserOrganization);
  const selectedOrg = useSelector(selectOrgHierarchySelectedOrg);
  const orgId = selectedOrg?._id;
  const orgs = useSelector(selectOrgList);
  const agentCount = useSelector(selectAgentCount);
  const maxAgents = org?.billing_subscription?.max_agents;
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState([]);;
  const [itemsCount, setItemsCount] = useState(0);
  const [filters, setFilters] = useState(null);

  const {
    enableChargebeeModule,
    enableOrgHierarchyModule,
    enableBulkAgentsRemoval,
    enableViewOnlyMode,
  } = useFlags();
  const labels = useContext(ConfigContext);

  const [bulkAction, setBulkAction] = useState<BulkActionState>({ action: null, selected: [] });
  const bulkDeleteAction = useMemo(() => MASS_DELETE_ACTION(labels), [labels]);

  const selectableTechnicians = useMemo(() => users?.filter(user => !user.removed && (isActionAllowed(profile, user) && user?.roles?.indexOf(UserRoles.Admin) === -1 )), [profile, users]);
  const AVAILABLE_COLUMNS = useMemo(() => [
    {
      key: 'select',
      label: <div className={`${(enableBulkAgentsRemoval && selectableTechnicians?.length !== 0) ? 'd-block' : 'd-none'}`}>
        <CFormGroup variant="custom-checkbox">
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label,jsx-a11y/label-has-associated-control */}
          <label htmlFor='select-all' className="mb-0">
            <CInputCheckbox
              id="select-all"
              name="select-all"
              checked={ selectableTechnicians?.length === bulkAction.selected.length && selectableTechnicians?.length !== 0 }
              onChange={ () => {
                if (bulkAction.selected.length === selectableTechnicians?.length) {
                  setBulkAction({ action: bulkAction.action, selected: [] });
                } else {
                  setBulkAction({ action: bulkAction.action, selected: [...selectableTechnicians] })
                }
              }}
            />
            <CTooltip placement="top" content="Select All">
              <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableAppt"/>
            </CTooltip>
          </label>
        </CFormGroup>
      </div>,
      sorter: false,
      filter: false,
    },
    {
      key: 'id',
      label: <div>ID
        <CTooltip placement="top" content={`Unique number identifier for ${labels?.agent?.toLowerCase()}/driver`}>
          <CImg src={tooltip} className="glympser-toolTip glympser-toolTip--jobListTableStatus"/>
        </CTooltip>
      </div>,
      filter: true,
    },
    {
      key: 'avatar',
      label: '',
      filter: false,
    },
    {
      key: 'name',
      label: 'Name',
      filter: true,
    },
    {
      key: 'phone',
      label: 'Phone',
      filter: true,
    },
    {
      key: 'email',
      label: 'Email',
      filter: true,
    },
    {
      key: 'roles',
      label: 'Roles',
      filter: true,
    },
    ...(enableOrgHierarchyModule ? [{
      key: 'orgId',
      label: 'Org ID',
      filter: true
    }, {
      key: 'orgName',
      label: 'Org Name',
      filter: true
    }] : []),
    ...(!enableViewOnlyMode ? [{
      key: 'actions',
      label: '',
      sorter: false,
      filter: false,
    }] : []),
  ], [labels, bulkAction, users, enableViewOnlyMode, enableOrgHierarchyModule]);

  useEffect(() => {
    if (orgId) {
      dispatch(new LoadUsers({
        orgId,
        includeChildren: enableOrgHierarchyModule
      }));
    }
  }, [orgId]);

  const transformedUsers = useMemo(() => {
    return users?.map((user) => {
      return {
        ...user,
        orgName: orgs?.find(value => value?._id === user?.orgId)?.name ?? '',
      };
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const filteredByPhoneNumber = filters?.phone ? transformedUsers?.filter((user) => {
      return user?.info?.phone?.includes(filters?.phone)
    }) : transformedUsers;

    return filteredByPhoneNumber?.filter(user => {
      return !user.removed && (selectedOrgs?.length === 0 || find(selectedOrgs, {value : user?.orgId}));
    });
  }, [transformedUsers, selectedOrgs, filters?.phone]);

  const handleColumnFilterChange = (filterValues: object) => {
    setFilters(filterValues);
  }

  return (
    <>
      <AddTechnicianModal show={ showModal } close={ () => setShowModal(false) }/>
      <CRow>
        <CCol lg="12">
          <CCard id="agents-page" className="glympser-container--roundedWithShadow mt-4">
            <CCardHeader className='border-0 glympser-roundedTop'>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  { enableChargebeeModule && agentCount && maxAgents && `${agentCount} of ${maxAgents} licenses used`}
                </div>
                <div className="card-header-actions d-flex align-items-center glympser-buttonContainer">
                  {
                    enableOrgHierarchyModule ? (
                      <OrgHierarchyFilter
                        handleChange={(items) => {
                          setSelectedOrgs(items)
                        }}
                        options={orgs?.reduce((acc, item) => {
                          if (item.hierarchy?.indexOf(selectedOrg._id) > -1 || item._id === selectedOrg._id) {
                            acc.push({
                              label: item.name,
                              value: item._id
                            });
                          }
                          return acc;
                        }, []) ?? []}
                        selectedOrgs={selectedOrgs}/>
                    ) : null
                  }
                  <div>
                    {
                    (enableBulkAgentsRemoval && !enableViewOnlyMode) ? (
                      <CButton
                        type="button"
                        color="primary"
                        className="mr-1 bg-color-orange border-0 roundedBtn font-weight-xbold"
                        disabled={ !bulkAction?.selected?.length }
                        onClick={ () => {
                          bulkDeleteAction?.action?.(dispatch, bulkAction.selected, () => setBulkAction({ action: null, selected: [] }));
                        }}
                      >
                        {bulkDeleteAction?.label}
                      </CButton>
                    ): null
                  }
                  { !enableViewOnlyMode && <CButton id="add-technician-button" type="button" color="primary" className="mr-1 bg-color-orange border-0 roundedBtn font-weight-xbold" onClick={ () => setShowModal(true) }>Add {labels.agent}</CButton>}
                  </div>
                </div>
              </div>
            </CCardHeader>

            <div className='job-list-container'>
              <CDataTable
                items={filteredUsers}
                fields={AVAILABLE_COLUMNS}
                loading={isLoading}
                hover
                itemsPerPage={ITEMS_PER_PAGE}
                striped
                columnFilter
                onColumnFilterChange={handleColumnFilterChange}
                pagination={{
                  addListClass: "ml-2"
                }}
                onPageChange={(page) => setCurrentPage(page)}
                onFilteredItemsChange={(data) => setItemsCount(data?.length || 0)}
                underTableSlot={filteredUsers && filteredUsers.length > 0 && <TableStats page={currentPage} total={itemsCount} pageSize={ITEMS_PER_PAGE} />}
                scopedSlots={{
                  'select': (technician: UserInfo) => {
                    const { id } = technician;

                    if (!isActionAllowed(profile, technician) || technician?.roles?.indexOf(UserRoles.Admin) !== -1 || !enableBulkAgentsRemoval) {
                      // eslint-disable-next-line jsx-a11y/control-has-associated-label
                      return <td className="checkbox-column" />;
                    }

                    const isChecked = bulkAction.selected.find(val => val.id === id) !== undefined;

                    return (
                      <td className="checkbox-column" >
                        <CFormGroup variant="custom-checkbox" inline>
                          <CInputCheckbox
                            checked={ isChecked }
                            onChange={ () => {
                              if (isChecked) {
                                setBulkAction({
                                  ...bulkAction,
                                  selected: bulkAction.selected.filter(val => val.id !== id),
                                });
                              } else {
                                setBulkAction({
                                  ...bulkAction,
                                  selected: [...bulkAction.selected, technician],
                                });
                              }
                            }}
                          />
                        </CFormGroup>
                      </td>
                    );
                  },
                  'actions': (technician: UserInfo) => {
                    return (
                      <td style={{ width: '1%'}}>
                        {/* eslint-disable-next-line react/destructuring-assignment */}
                        { isActionAllowed(profile, technician) ? <TechnicianActions key={technician?.info?.phone} technician={ technician } /> : null }
                      </td>
                    );
                  },
                  'phone': ({ info }: UserInfo) => {
                    const formatPhone = (phone) => {
                      try {
                        return parsePhoneNumber(info.phone).formatNational();
                      } catch (e) {
                        return phone;
                      }
                    };

                    return (
                      <td>
                        { formatPhone(info.phone) }
                      </td>
                    );
                  },
                  'avatar': (technician: UserInfo) => {
                    const { avatar } = technician;
                    return (
                      <td style={{ width: '1%' }} className="text-center">
                        <div className="c-avatar">
                          <img src={avatar?.url || emptyAvatar} className="c-avatar-img" alt="user avatar" />
                        </div>
                      </td>
                    );
                  },
                  'roles': (technician: UserInfo) => {
                    const { roles } = technician;
                    return <td>{
                      (roles || []).map((role: string) => (
                        <CBadge color="secondary mr-2" key={role}>{role}</CBadge>
                      ))
                    }</td>
                  },
                }}
              />
            </div>
          </CCard>
        </CCol>
      </CRow>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: state?.userManagement?.users?.data,
  isLoading: state?.userManagement?.users?.isLoading,
  profile: state?.profile?.userInfo,
});

export default connect(mapStateToProps)(TechniciansListPage);
